import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import AddressInput from "@/shared/components/AddressInput";
import { toast } from "sonner";
import { Camera, Trash2, Plus, MapPin, User, Home, Phone, Mail, Sun, Moon, Smartphone } from "lucide-react";
import { useTheme } from "@/shared/context/ThemeContext";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [picture, setPicture] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [book, setBook] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [savingAddress, setSavingAddress] = useState(false);

  useEffect(() => {
    if (user === false) nav("/login?redirect=/profile");
    if (user && user.id) {
      setName(user.name || "");
      setPhone(user.phone || "");
      setHomeAddress(user.home_address || "");
      setPicture(user.profile_picture || null);
      setBook(user.address_book || []);
    }
  }, [user, nav]);

  const saveProfile = async (e) => {
    e?.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", {
        name, phone, home_address: homeAddress, profile_picture: picture,
      });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingProfile(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_000_000) { toast.error("Image too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setPicture(reader.result);
    reader.readAsDataURL(f);
  };

  const addAddress = async (e) => {
    e.preventDefault();
    if (!newLabel || !newAddress) { toast.error("Label and address are required"); return; }
    setSavingAddress(true);
    try {
      const { data } = await api.post("/auth/me/addresses", { label: newLabel, address: newAddress });
      setBook(data);
      setNewLabel(""); setNewAddress("");
      toast.success("Address saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingAddress(false); }
  };

  const removeAddress = async (id) => {
    try {
      const { data } = await api.delete(`/auth/me/addresses/${id}`);
      setBook(data);
      toast.success("Address removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!user || user === false) return null;

  const initials = (name || user.email || "U").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-16" data-testid="profile-page">
      <div className="label-xs text-[#ed6500] mb-3">Your account</div>
      <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-10 lg:mb-14">
        Profile & addresses.
      </h1>

      {/* Avatar + user info */}
      <form onSubmit={saveProfile} className="space-y-8 border hairline bg-white p-6 sm:p-8" data-testid="profile-card">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-[#FCEADE] border hairline flex items-center justify-center overflow-hidden" data-testid="profile-avatar">
              {picture ? (
                <img src={picture} alt="avatar" className="w-full h-full object-cover"/>
              ) : (
                <div className="font-serif text-2xl text-[#ed6500]">{initials}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 bg-[#0A1128] text-[#F9F7F4] w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#ed6500] transition-colors"
              aria-label="Change avatar"
              data-testid="avatar-upload-btn"
            >
              <Camera size={14}/>
            </button>
            <input ref={fileRef} onChange={onPickFile} type="file" accept="image/*" className="hidden" data-testid="avatar-file-input"/>
          </div>
          <div>
            <div className="font-serif text-2xl">{user.name}</div>
            <div className="text-sm text-[#515E7B] flex items-center gap-1.5"><Mail size={12}/> {user.email}</div>
            {picture && (
              <button type="button" onClick={() => setPicture(null)} className="text-[11px] text-[#ed6500] hover:underline mt-1" data-testid="avatar-remove-btn">
                Remove photo
              </button>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
          <div>
            <div className="label-xs mb-2 flex items-center gap-1.5"><User size={12}/> Full name</div>
            <input className="input-line" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"/>
          </div>
          <div>
            <div className="label-xs mb-2 flex items-center gap-1.5"><Phone size={12}/> Phone</div>
            <input className="input-line" type="tel" placeholder="Optional" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input"/>
          </div>
        </div>

        <div>
          <div className="label-xs mb-2 flex items-center gap-1.5"><Home size={12}/> Home address</div>
          <AddressInput value={homeAddress} onChange={setHomeAddress} placeholder="Your primary address" testId="profile-home-address"/>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t hairline">
          <button type="button" onClick={() => logout().then(() => nav("/"))} className="text-[11px] sm:text-xs tracking-widest uppercase px-4 py-2.5 border hairline hover:bg-[#F9F7F4]" data-testid="profile-logout-btn">
            Sign out
          </button>
          <button type="submit" disabled={savingProfile} className="btn-primary text-[11px] sm:text-xs tracking-widest uppercase disabled:opacity-60" data-testid="profile-save-btn">
            {savingProfile ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      {/* Address book */}
      <div className="mt-12" data-testid="address-book">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="label-xs text-[#5c97f7]">Address book</div>
            <div className="font-serif text-2xl sm:text-3xl mt-1">Saved places.</div>
          </div>
          <div className="text-xs text-[#515E7B]">{book.length} saved</div>
        </div>

        <div className="border hairline bg-white">
          {book.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#515E7B]" data-testid="address-book-empty">
              No saved addresses yet. Add your frequent destinations below.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E1D8]">
              {book.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-4 p-4" data-testid={`address-row-${a.id}`}>
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="mt-0.5 text-[#5c97f7] shrink-0"/>
                    <div>
                      <div className="text-sm font-semibold">{a.label}</div>
                      <div className="text-xs text-[#515E7B]">{a.address}</div>
                    </div>
                  </div>
                  <button onClick={() => removeAddress(a.id)} className="text-[#515E7B] hover:text-[#ed6500] p-1" aria-label="Remove address" data-testid={`address-remove-${a.id}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={addAddress} className="border-t hairline bg-[#F9F7F4] p-4 grid sm:grid-cols-[140px_1fr_auto] gap-3 items-end" data-testid="address-add-form">
            <div>
              <div className="label-xs mb-2">Label</div>
              <input className="input-line text-sm" placeholder="Home / Work" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="address-new-label"/>
            </div>
            <div>
              <div className="label-xs mb-2">Address</div>
              <AddressInput value={newAddress} onChange={setNewAddress} placeholder="Full address" testId="address-new-address"/>
            </div>
            <button type="submit" disabled={savingAddress} className="btn-primary text-[11px] tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-60 h-[40px]" data-testid="address-add-btn">
              <Plus size={14}/> Add
            </button>
          </form>
        </div>

        {/* ============ Appearance / theme ============ */}
        <div className="mt-10 border hairline bg-white" data-testid="profile-appearance">
          <div className="border-b hairline px-4 sm:px-6 py-3 flex items-center gap-2">
            <Sun size={14} className="text-[#ed6500]" />
            <h2 className="label-xs">Appearance</h2>
          </div>
          <div className="p-4 sm:p-6">
            <p className="text-xs text-[#515E7B] mb-3">
              Choose how Link looks. System matches your device.
            </p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Theme preference">
              {[
                { id: "system", label: "System", Icon: Smartphone },
                { id: "light",  label: "Light",  Icon: Sun },
                { id: "dark",   label: "Dark",   Icon: Moon },
              ].map(({ id, label, Icon }) => {
                const active = preference === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setPreference(id)}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 border hairline text-[11px] tracking-widest uppercase font-semibold transition-colors rounded-2xl ${
                      active
                        ? "bg-[#0A1128] text-[#F9F7F4] border-[#0A1128]"
                        : "bg-white text-[#515E7B] hover:border-[#ed6500]"
                    }`}
                    data-testid={`theme-option-${id}`}
                  >
                    <Icon size={16} strokeWidth={1.6} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
